/**
 * Public Security & Compliance Overview Page
 * Client-facing branded document for prospects
 */
import React from 'react';
import { Link } from 'react-router-dom';
import { 
  ShieldCheck, Lock, Server, Key, Users, Code, Bell, Search, 
  Database, Shield, Briefcase, CheckCircle, Clock, ArrowLeft,
  Download, Mail, Globe, FileText
} from 'lucide-react';

const API_URL = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/$/, '');

export default function SecurityOverviewPage() {
  const handleDownloadPDF = async () => {
    try {
      const response = await fetch(`${API_URL}/api/trust-center/security-overview/download`, {
        credentials: 'include'
      });
      
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'Eyvo_Procurement_Security_Overview.pdf';
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
      }
    } catch (error) {
      console.error('Download error:', error);
    }
  };

  return (
    <div className="security-overview-page">
      <div className="security-overview-container">
        {/* Header */}
        <header className="security-header">
          <div className="header-nav">
            <Link to="/trust-center" className="back-link">
              <ArrowLeft size={18} />
              Back to Trust Center
            </Link>
            <button onClick={handleDownloadPDF} className="download-btn" data-testid="download-overview-pdf">
              <Download size={18} />
              Download PDF
            </button>
          </div>
          
          <div className="header-brand">
            <div className="brand-logo">
              <img src="/eyvo-logo.png" alt="Eyvo" className="overview-logo-img" />
            </div>
            <h1>Security & Compliance Overview</h1>
            <p className="header-subtitle">Self-assessed controls for enterprise procurement security</p>
          </div>
        </header>

        {/* Content */}
        <main className="security-content">
          {/* Introduction */}
          <section className="overview-section">
            <div className="section-icon intro-icon">
              <Globe size={24} />
            </div>
            <div className="section-content">
              <h2>1. Introduction</h2>
              <p>
                Eyvo is a cloud-based eProcurement platform designed with security, compliance, and data integrity at its core. 
                We maintain a comprehensive security and compliance program with self-assessed controls aligned to SOC 2 Trust Services Criteria.
              </p>
              <p>
                Our platform helps organizations streamline procurement while ensuring the highest standards of data protection 
                and regulatory compliance.
              </p>
            </div>
          </section>

          {/* Compliance Framework */}
          <section className="overview-section">
            <div className="section-icon compliance-icon">
              <FileText size={24} />
            </div>
            <div className="section-content">
              <h2>2. Compliance Framework</h2>
              <p>Eyvo's controls are self-assessed and align with industry-leading standards:</p>
              <div className="compliance-badges">
                <div className="compliance-badge">
                  <ShieldCheck size={20} />
                  <div>
                    <strong>SOC 2 (Self-Assessed)</strong>
                    <span>Security, Availability, Confidentiality, Processing Integrity, Privacy</span>
                  </div>
                </div>
                <div className="compliance-badge">
                  <Shield size={20} />
                  <div>
                    <strong>NIST SP 800-30 (Self-Assessed)</strong>
                    <span>Risk Management Framework</span>
                  </div>
                </div>
                <div className="compliance-badge">
                  <Lock size={20} />
                  <div>
                    <strong>GDPR & CCPA/CPRA</strong>
                    <span>Data Privacy Regulations</span>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Platform Security */}
          <section className="overview-section">
            <div className="section-icon security-icon">
              <Server size={24} />
            </div>
            <div className="section-content">
              <h2>3. Platform Security</h2>
              
              <h3>Infrastructure</h3>
              <ul className="security-list">
                <li><CheckCircle size={16} /> Hosted on Microsoft Azure (SOC 2 certified infrastructure)</li>
                <li><CheckCircle size={16} /> Multi-region redundancy</li>
                <li><CheckCircle size={16} /> Network segmentation and firewall protection</li>
                <li><CheckCircle size={16} /> DDoS protection and WAF</li>
              </ul>

              <h3>Encryption</h3>
              <div className="encryption-grid">
                <div className="encryption-item">
                  <Key size={18} />
                  <div>
                    <strong>Data in Transit</strong>
                    <span>TLS 1.2+</span>
                  </div>
                </div>
                <div className="encryption-item">
                  <Database size={18} />
                  <div>
                    <strong>Data at Rest</strong>
                    <span>AES-256</span>
                  </div>
                </div>
                <div className="encryption-item">
                  <Lock size={18} />
                  <div>
                    <strong>Key Management</strong>
                    <span>Azure Key Vault</span>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Access Control */}
          <section className="overview-section">
            <div className="section-icon access-icon">
              <Users size={24} />
            </div>
            <div className="section-content">
              <h2>4. Access Control</h2>
              <ul className="security-list">
                <li><CheckCircle size={16} /> Role-Based Access Control (RBAC)</li>
                <li><CheckCircle size={16} /> Multi-Factor Authentication (MFA) enforced</li>
                <li><CheckCircle size={16} /> Quarterly access reviews</li>
                <li><CheckCircle size={16} /> Immediate deprovisioning on termination</li>
                <li><CheckCircle size={16} /> SSO integration support (SAML, OAuth)</li>
              </ul>
            </div>
          </section>

          {/* Application Security */}
          <section className="overview-section">
            <div className="section-icon app-icon">
              <Code size={24} />
            </div>
            <div className="section-content">
              <h2>5. Application Security</h2>
              <ul className="security-list">
                <li><CheckCircle size={16} /> Secure SDLC with OWASP standards</li>
                <li><CheckCircle size={16} /> Code reviews and security testing (SAST, DAST)</li>
                <li><CheckCircle size={16} /> Input validation and injection protection</li>
                <li><CheckCircle size={16} /> Regular penetration testing by third parties</li>
                <li><CheckCircle size={16} /> Dependency vulnerability scanning</li>
              </ul>
            </div>
          </section>

          {/* Monitoring & Incident Response */}
          <section className="overview-section">
            <div className="section-icon monitoring-icon">
              <Bell size={24} />
            </div>
            <div className="section-content">
              <h2>6. Monitoring & Incident Response</h2>
              <ul className="security-list">
                <li><CheckCircle size={16} /> Centralized logging and SIEM monitoring</li>
                <li><CheckCircle size={16} /> Real-time alerting for critical events</li>
                <li><CheckCircle size={16} /> Defined incident response procedures</li>
                <li><CheckCircle size={16} /> 24/7 monitoring via Azure Security Center</li>
                <li><CheckCircle size={16} /> Documented escalation procedures</li>
              </ul>
            </div>
          </section>

          {/* Vulnerability Management */}
          <section className="overview-section">
            <div className="section-icon vuln-icon">
              <Search size={24} />
            </div>
            <div className="section-content">
              <h2>7. Vulnerability Management</h2>
              <ul className="security-list">
                <li><CheckCircle size={16} /> Weekly vulnerability scanning</li>
                <li><CheckCircle size={16} /> Annual penetration testing</li>
              </ul>
              
              <h3>Remediation SLAs</h3>
              <div className="sla-grid">
                <div className="sla-item critical">
                  <Clock size={16} />
                  <strong>Critical</strong>
                  <span>24 hours</span>
                </div>
                <div className="sla-item high">
                  <Clock size={16} />
                  <strong>High</strong>
                  <span>7 days</span>
                </div>
                <div className="sla-item medium">
                  <Clock size={16} />
                  <strong>Medium</strong>
                  <span>30 days</span>
                </div>
              </div>
            </div>
          </section>

          {/* Data Protection */}
          <section className="overview-section">
            <div className="section-icon data-icon">
              <Database size={24} />
            </div>
            <div className="section-content">
              <h2>8. Data Protection & Privacy</h2>
              <ul className="security-list">
                <li><CheckCircle size={16} /> Data encryption and tenant isolation</li>
                <li><CheckCircle size={16} /> Strict access controls</li>
                <li><CheckCircle size={16} /> No sale of personal data</li>
                <li><CheckCircle size={16} /> Compliance with GDPR and CCPA</li>
                <li><CheckCircle size={16} /> Data subject rights support (access, deletion, portability)</li>
              </ul>
            </div>
          </section>

          {/* Vendor Security */}
          <section className="overview-section">
            <div className="section-icon vendor-icon">
              <Briefcase size={24} />
            </div>
            <div className="section-content">
              <h2>9. Vendor & Supplier Security</h2>
              <ul className="security-list">
                <li><CheckCircle size={16} /> Formal vendor risk management program</li>
                <li><CheckCircle size={16} /> Security assessments for all critical vendors</li>
                <li><CheckCircle size={16} /> SOC 2 / ISO certification requirements</li>
                <li><CheckCircle size={16} /> Ongoing monitoring and annual reviews</li>
                <li><CheckCircle size={16} /> Contractual security requirements</li>
              </ul>
            </div>
          </section>

          {/* Processing Integrity */}
          <section className="overview-section">
            <div className="section-icon integrity-icon">
              <CheckCircle size={24} />
            </div>
            <div className="section-content">
              <h2>10. Processing Integrity</h2>
              <p>Eyvo ensures:</p>
              <ul className="security-list">
                <li><CheckCircle size={16} /> Accurate financial calculations</li>
                <li><CheckCircle size={16} /> Enforced approval workflows</li>
                <li><CheckCircle size={16} /> Complete transaction processing</li>
                <li><CheckCircle size={16} /> Reconciliation with third-party systems</li>
                <li><CheckCircle size={16} /> Audit trails for all procurement actions</li>
              </ul>
            </div>
          </section>

          {/* Business Continuity */}
          <section className="overview-section">
            <div className="section-icon bc-icon">
              <Shield size={24} />
            </div>
            <div className="section-content">
              <h2>11. Business Continuity & Availability</h2>
              <ul className="security-list">
                <li><CheckCircle size={16} /> Automated backups and point-in-time recovery</li>
                <li><CheckCircle size={16} /> Disaster recovery testing</li>
                <li><CheckCircle size={16} /> High availability architecture</li>
              </ul>
              
              <div className="rto-rpo">
                <div className="metric">
                  <strong>RTO</strong>
                  <span>4 hours</span>
                  <small>Recovery Time Objective</small>
                </div>
                <div className="metric">
                  <strong>RPO</strong>
                  <span>1 hour</span>
                  <small>Recovery Point Objective</small>
                </div>
              </div>
            </div>
          </section>

          {/* Continuous Compliance */}
          <section className="overview-section">
            <div className="section-icon continuous-icon">
              <ShieldCheck size={24} />
            </div>
            <div className="section-content">
              <h2>12. Continuous Compliance</h2>
              <ul className="security-list">
                <li><CheckCircle size={16} /> Continuous control monitoring</li>
                <li><CheckCircle size={16} /> Internal audits and control testing</li>
                <li><CheckCircle size={16} /> Ongoing security improvements</li>
                <li><CheckCircle size={16} /> Regular policy reviews and updates</li>
                <li><CheckCircle size={16} /> Employee security awareness training</li>
              </ul>
            </div>
          </section>

          {/* Contact */}
          <section className="overview-section contact-section">
            <div className="section-icon contact-icon">
              <Mail size={24} />
            </div>
            <div className="section-content">
              <h2>13. Contact</h2>
              <p>For security inquiries or to request additional documentation:</p>
              <a href="mailto:security@eyvo.com" className="contact-email">
                <Mail size={18} />
                security@eyvo.com
              </a>
              <div className="trust-center-link">
                <p>Visit our Trust Center for real-time compliance status:</p>
                <Link to="/trust-center">
                  <ShieldCheck size={18} />
                  Eyvo Trust Center
                </Link>
              </div>
            </div>
          </section>
        </main>

        {/* Footer */}
        <footer className="security-footer">
          <div className="footer-brand">
            <img src="/eyvo-logo.png" alt="Eyvo" className="footer-logo-img" />
          </div>
          <p>© {new Date().getFullYear()} Eyvo Procurement. All rights reserved. | www.eyvo.com | security@eyvo.com</p>
          <p className="footer-note">This document contains self-assessed compliance information for informational purposes.</p>
        </footer>
      </div>
    </div>
  );
}
